import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { useLogin } from '../hooks/login'
import { settingsInitialStore, type SettingsStore } from '../views/Settings/SettingsScturcture'

type SettingsContextValue = {
  store: SettingsStore
  set: <K extends keyof SettingsStore>(key: K, value: SettingsStore[K]) => void
  reset: () => void
  replace: (partial: Partial<SettingsStore>) => void
}

const SettingsContext = createContext<SettingsContextValue | undefined>(undefined)

const buildStorageKey = (userId?: number | string | null) => (userId ? `settings_${userId}` : null)

const loadFromLocalStorage = (userId?: number | string | null): SettingsStore => {
  try {
    const key = buildStorageKey(userId)
    if (!key) return { ...settingsInitialStore }
    const raw = window.localStorage.getItem(key)
    const parsed = raw ? (JSON.parse(raw) as Partial<SettingsStore>) : {}
    const merged: SettingsStore = { ...settingsInitialStore, ...parsed }
    return merged
  } catch {
    return { ...settingsInitialStore }
  }
}

const persistToLocalStorage = (userId: number | string | null | undefined, store: SettingsStore) => {
  const key = buildStorageKey(userId)
  if (!key) return
  window.localStorage.setItem(key, JSON.stringify(store))
}

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useLogin()
  const userId = user?.id ?? null

  const [store, setStore] = useState<SettingsStore>(() => loadFromLocalStorage(userId))

  // Reload settings when user changes
  useEffect(() => {
    // Store fresh user id in localStorage to access it from 'un-react' util function
    window.localStorage.setItem('currentUserId', userId?.toString() || '')

    setStore(loadFromLocalStorage(userId))
  }, [userId])

  // Persist when store changes for this user
  useEffect(() => {
    persistToLocalStorage(userId, store)
  }, [userId, store])

  const api = useMemo<SettingsContextValue>(
    () => ({
      store,
      set: (key, value) => {
        setStore(prev => ({ ...prev, [key]: value }))
      },
      replace: partial => {
        setStore(prev => ({ ...prev, ...partial }))
      },
      reset: () => {
        setStore({ ...settingsInitialStore })
      },
    }),
    [store],
  )

  return <SettingsContext.Provider value={api}>{children}</SettingsContext.Provider>
}

// "Un-react" way to use settings
export const getSetting = (key: keyof SettingsStore): SettingsStore[keyof SettingsStore] | null => {
  const userId = window.localStorage.getItem('currentUserId')
  if (!userId) return null
  const store = loadFromLocalStorage(userId)
  return store[key] ?? null
}

export const useSettings = (): SettingsContextValue => {
  const ctx = useContext(SettingsContext)
  if (!ctx) {
    throw new Error('useSettings must be used within a SettingsProvider')
  }
  return ctx
}

export const useSettingsStore = () => {
  const { store } = useSettings()
  return store
}
